/**
 * Admin → Transporters
 * --------------------
 * Phase 1 management surface for the Armstrong Transport marketplace.
 * Lists every transporter row currently in the system and offers a
 * "Seed verified transporter" form for manual onboarding (the
 * self-service `/transport/apply` flow lands in Phase 3).
 *
 * Once Phase 3 ships, this page also becomes the home for the
 * application review queue. Phase 6 layers disputes/strikes on top.
 *
 * Design choices
 * - Two-pane layout (list left, seed form right) so admin doesn't
 *   navigate away between adding and verifying their work.
 * - Inline status toggle (pause/suspend/terminate) per row — every
 *   transition is audit-logged backend-side and reflected in the row
 *   without a page reload.
 * - All form fields map 1:1 to the `TransporterSeedIn` Pydantic
 *   model so validation errors come through cleanly.
 */
import React, { useState, useEffect, useCallback } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { TruckIcon as Truck, ShieldCheckIcon as ShieldCheck, ArrowLeftIcon as ArrowLeft, CheckCircleIcon as CheckCircle, PauseIcon as Pause, ProhibitIcon as Prohibit, CopyIcon as Copy } from "@phosphor-icons/react";

const STATUS_TONE = {
  active: "text-emerald-700 bg-emerald-50 border-emerald-200",
  paused: "text-amber-700 bg-amber-50 border-amber-200",
  suspended: "text-orange-700 bg-orange-50 border-orange-200",
  terminated: "text-red-700 bg-red-50 border-red-200",
};

const BLANK_SEED = {
  display_name: "",
  legal_name: "",
  email: "",
  phone: "",
  usda_license_number: "",
  usda_license_expiry: "",
  insurance_carrier: "",
  insurance_policy_number: "",
  insurance_expiry: "",
  insurance_liability_usd: 300000,
  stripe_connect_account_id: "",
  stripe_payouts_enabled: false,
  service_area_states: "",
  rig_photo_url: "",
  kennel_photo_url: "",
  notes: "",
};

export default function AdminTransporters() {
  const [transporters, setTransporters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seed, setSeed] = useState(BLANK_SEED);
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState("roster");  // 'roster' | 'orders' | 'applications'

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/transporters");
      setTransporters(res.data.transporters || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't load transporters.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSeed = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Service area arrives as a comma-separated string; backend
      // expects an array of two-letter codes. Trim each + drop empties.
      const states = seed.service_area_states
        .split(",")
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);
      const payload = {
        ...seed,
        service_area_states: states,
        // Server validates int >= 300000; coerce here so an admin
        // typing "300000" as a string still posts cleanly.
        insurance_liability_usd: Number(seed.insurance_liability_usd) || 300000,
        // Drop empty optional strings so the server doesn't store "".
        legal_name: seed.legal_name || null,
        phone: seed.phone || null,
        usda_license_expiry: seed.usda_license_expiry || null,
        insurance_expiry: seed.insurance_expiry || null,
        stripe_connect_account_id: seed.stripe_connect_account_id || null,
        rig_photo_url: seed.rig_photo_url || null,
        kennel_photo_url: seed.kennel_photo_url || null,
        notes: seed.notes || null,
      };
      const res = await api.post("/admin/transporters", payload);
      toast.success("Transporter seeded", {
        description: `${res.data.transporter.display_name} is now active.`,
      });
      setTransporters((prev) => [res.data.transporter, ...prev]);
      setSeed(BLANK_SEED);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't seed transporter.");
    } finally {
      setSubmitting(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      const res = await api.patch(`/admin/transporters/${id}/status`, { status });
      setTransporters((prev) =>
        prev.map((t) => (t.id === id ? res.data.transporter : t))
      );
      toast.success(`Status → ${status}`);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't update status.");
    }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-transporters-page">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-[12px] text-[#6B6B6B] hover:text-ink mb-3"
          data-testid="admin-transporters-back"
        >
          <ArrowLeft size={14} /> Back to Admin
        </Link>
        <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
          <h1 className="font-spectral text-4xl tracking-tight flex items-center gap-3">
            <Truck size={32} weight="duotone" className="text-ink" />
            Transporters
          </h1>
          <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#6B6B6B]">
            Phase 1 · Manual onboarding
          </span>
        </div>
        <p className="text-[13px] text-[#6B6B6B] max-w-2xl mb-6 leading-relaxed">
          Verified transporters appear in the directory and become eligible
          for bookings the second the booking flow ships (Phase 2). Until
          the self-service application flow lands (Phase 3), use the seed
          form on the right to manually onboard hand-verified haulers.
        </p>

        {/* Tab switcher */}
        <div className="flex gap-1 border-b border-[#E5E5E5] mb-6">
          <button
            onClick={() => setTab("roster")}
            className={`px-4 py-2 text-[13px] font-semibold border-b-2 transition-colors ${
              tab === "roster"
                ? "border-ink text-ink"
                : "border-transparent text-[#6B6B6B] hover:text-ink"
            }`}
            data-testid="tab-roster"
          >
            Active roster
          </button>
          <button
            onClick={() => setTab("orders")}
            className={`px-4 py-2 text-[13px] font-semibold border-b-2 transition-colors ${
              tab === "orders"
                ? "border-ink text-ink"
                : "border-transparent text-[#6B6B6B] hover:text-ink"
            }`}
            data-testid="tab-orders"
          >
            Open requests
          </button>
          <button
            onClick={() => setTab("applications")}
            className={`px-4 py-2 text-[13px] font-semibold border-b-2 transition-colors ${
              tab === "applications"
                ? "border-ink text-ink"
                : "border-transparent text-[#6B6B6B] hover:text-ink"
            }`}
            data-testid="tab-applications"
          >
            Applications
          </button>
        </div>

        {tab === "applications" ? (
          <ApplicationsPanel />
        ) : tab === "orders" ? (
          <OrdersPanel transporters={transporters} />
        ) : (
          <div className="grid lg:grid-cols-[1.4fr,1fr] gap-6">
          {/* Left: directory list */}
          <section
            className="rounded-2xl border border-[#E5E5E5] bg-white p-5"
            data-testid="admin-transporters-list-card"
          >
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-spectral text-xl tracking-tight">
                Active roster
              </h2>
              <span className="text-[12px] text-[#6B6B6B]">
                {loading ? "…" : `${transporters.length} total`}
              </span>
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
              </div>
            ) : transporters.length === 0 ? (
              <div className="text-center py-12 text-[#6B6B6B] text-[13px]">
                No transporters yet. Use the seed form to onboard your first hauler.
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-transporters-list">
                {transporters.map((t) => (
                  <TransporterRow key={t.id} t={t} onStatus={setStatus} onReload={load} />
                ))}
              </div>
            )}
          </section>

          {/* Right: seed form */}
          <section
            className="rounded-2xl border border-[#E5E5E5] bg-white p-5 self-start"
            data-testid="admin-transporters-seed-card"
          >
            <h2 className="font-spectral text-xl tracking-tight mb-1">
              Seed verified transporter
            </h2>
            <p className="text-[12px] text-[#6B6B6B] mb-4 leading-relaxed">
              Use after manually inspecting the transporter's USDA license,
              insurance certificate, and Stripe Connect onboarding status.
            </p>
            <form onSubmit={onSeed} className="space-y-3" data-testid="admin-transporter-seed-form">
              <Field
                label="Display name *"
                value={seed.display_name}
                onChange={(v) => setSeed({ ...seed, display_name: v })}
                placeholder="Bryson Family Hauling"
                required
                testId="seed-display-name"
              />
              <Field
                label="Legal / DBA name"
                value={seed.legal_name}
                onChange={(v) => setSeed({ ...seed, legal_name: v })}
                placeholder="Bryson Family Hauling, LLC"
                testId="seed-legal-name"
              />
              <div className="grid grid-cols-2 gap-2">
                <Field
                  label="Email *"
                  type="email"
                  value={seed.email}
                  onChange={(v) => setSeed({ ...seed, email: v })}
                  required
                  testId="seed-email"
                />
                <Field
                  label="Phone"
                  value={seed.phone}
                  onChange={(v) => setSeed({ ...seed, phone: v })}
                  placeholder="+1 ..."
                  testId="seed-phone"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field
                  label="USDA license # *"
                  value={seed.usda_license_number}
                  onChange={(v) => setSeed({ ...seed, usda_license_number: v })}
                  required
                  testId="seed-usda"
                />
                <Field
                  label="USDA expiry"
                  type="date"
                  value={seed.usda_license_expiry}
                  onChange={(v) => setSeed({ ...seed, usda_license_expiry: v })}
                  testId="seed-usda-expiry"
                />
              </div>
              <Field
                label="Insurance carrier *"
                value={seed.insurance_carrier}
                onChange={(v) => setSeed({ ...seed, insurance_carrier: v })}
                placeholder="Progressive Commercial"
                required
                testId="seed-ins-carrier"
              />
              <div className="grid grid-cols-2 gap-2">
                <Field
                  label="Policy # *"
                  value={seed.insurance_policy_number}
                  onChange={(v) => setSeed({ ...seed, insurance_policy_number: v })}
                  required
                  testId="seed-ins-policy"
                />
                <Field
                  label="Policy expiry"
                  type="date"
                  value={seed.insurance_expiry}
                  onChange={(v) => setSeed({ ...seed, insurance_expiry: v })}
                  testId="seed-ins-expiry"
                />
              </div>
              <Field
                label="Liability $ (min 300000) *"
                type="number"
                value={seed.insurance_liability_usd}
                onChange={(v) => setSeed({ ...seed, insurance_liability_usd: v })}
                required
                testId="seed-liability"
              />
              <Field
                label="Stripe Connect account ID"
                value={seed.stripe_connect_account_id}
                onChange={(v) => setSeed({ ...seed, stripe_connect_account_id: v })}
                placeholder="acct_..."
                testId="seed-stripe-acct"
              />
              <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={seed.stripe_payouts_enabled}
                  onChange={(e) => setSeed({ ...seed, stripe_payouts_enabled: e.target.checked })}
                  data-testid="seed-payouts-enabled"
                />
                Stripe payouts enabled (Connect onboarding complete)
              </label>
              <Field
                label="Service area (comma-separated state codes)"
                value={seed.service_area_states}
                onChange={(v) => setSeed({ ...seed, service_area_states: v })}
                placeholder="TN, GA, AL, KY"
                testId="seed-states"
              />
              <Field
                label="Rig photo URL"
                value={seed.rig_photo_url}
                onChange={(v) => setSeed({ ...seed, rig_photo_url: v })}
                testId="seed-rig"
              />
              <Field
                label="Kennel photo URL"
                value={seed.kennel_photo_url}
                onChange={(v) => setSeed({ ...seed, kennel_photo_url: v })}
                testId="seed-kennel"
              />
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Notes</Label>
                <Textarea
                  value={seed.notes}
                  onChange={(e) => setSeed({ ...seed, notes: e.target.value })}
                  className="mt-1 text-[13px]"
                  rows={3}
                  placeholder="Anything you want recorded — preferred lanes, recurring breeders, special handling capabilities..."
                  data-testid="seed-notes"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full rounded-full bg-ink hover:bg-crimson text-white"
                data-testid="seed-submit"
              >
                {submitting ? "Seeding…" : "Seed transporter as active"}
              </Button>
            </form>
          </section>
          </div>
        )}
      </div>
    </AppShell>
  );
}

/**
 * OrdersPanel — buyer transport requests pipeline view.
 *
 * Shows every order grouped by status: pending assignment first
 * (action required), then awaiting payment (link sent), then booked
 * (live trips). Cancelled orders fold below.
 *
 * Clicking "Assign transporter" on a pending row opens a modal where
 * admin picks a transporter from the active roster + sets a price.
 * Submitting calls /api/admin/transport-orders/{id}/assign which
 * creates the Stripe Checkout session — the returned URL is shown
 * with a "Copy" button so admin can email it to the buyer.
 */
function OrdersPanel({ transporters }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const eligibleTransporters = transporters.filter(
    (t) => t.status === "active" && t.stripe_payouts_enabled && t.stripe_connect_account_id
  );

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/transport-orders");
      setOrders(res.data.orders || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't load orders.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onAssign = (updated) => {
    setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
  };
  const onCancel = async (id) => {
    if (!window.confirm("Cancel this order? This cannot be undone.")) return;
    try {
      await api.post(`/admin/transport-orders/${id}/cancel`);
      toast.success("Order cancelled");
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't cancel.");
    }
  };

  const groups = {
    open_pending_assign: orders.filter((o) => o.status === "open_pending_assign"),
    awaiting_payment: orders.filter((o) => o.status === "awaiting_payment"),
    booked: orders.filter((o) => o.status === "booked"),
    cancelled: orders.filter((o) => o.status === "cancelled"),
  };

  if (loading) return <Skeleton className="h-40" />;

  return (
    <div className="space-y-6" data-testid="orders-panel">
      <OrderGroup
        title="Pending assignment"
        tone="amber"
        orders={groups.open_pending_assign}
        renderAction={(o) => (
          <AssignDialog
            order={o}
            transporters={eligibleTransporters}
            onAssigned={onAssign}
            onCancel={onCancel}
          />
        )}
        emptyHint="No new requests yet. Share /transport/request with prospective buyers."
      />
      <OrderGroup
        title="Awaiting payment"
        tone="blue"
        orders={groups.awaiting_payment}
        renderAction={(o) => <CopyCheckoutLink order={o} />}
      />
      <OrderGroup
        title="Booked"
        tone="emerald"
        orders={groups.booked}
      />
      {groups.cancelled.length > 0 && (
        <OrderGroup
          title="Cancelled"
          tone="red"
          orders={groups.cancelled}
          collapsed
        />
      )}
    </div>
  );
}

function OrderGroup({ title, tone, orders, renderAction, emptyHint, collapsed }) {
  const [expanded, setExpanded] = useState(!collapsed);
  if (orders.length === 0 && !emptyHint) return null;
  return (
    <section className="border border-[#E5E5E5] bg-white rounded-2xl p-5">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-spectral text-lg tracking-tight">
          {title}
          <span className={`ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border bg-${tone}-50 border-${tone}-200 text-${tone}-700`}>
            {orders.length}
          </span>
        </h3>
        {collapsed && (
          <button onClick={() => setExpanded(!expanded)} className="text-[12px] text-[#6B6B6B] hover:text-ink">
            {expanded ? "Hide" : "Show"}
          </button>
        )}
      </div>
      {expanded && (
        orders.length === 0 ? (
          <p className="text-[12px] text-[#6B6B6B]">{emptyHint}</p>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => (
              <OrderRow key={o.id} order={o} renderAction={renderAction} />
            ))}
          </div>
        )
      )}
    </section>
  );
}

function OrderRow({ order, renderAction }) {
  return (
    <div
      className="border border-[#EAEAEA] rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3"
      data-testid={`order-row-${order.id}`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-medium">
          {order.pickup_location.city}, {order.pickup_location.state}
          <span className="text-[#9A9A9A] mx-1.5">→</span>
          {order.dropoff_location.city}, {order.dropoff_location.state}
        </p>
        <p className="text-[12px] text-[#6B6B6B] mt-0.5">
          {order.buyer_email}
          {order.dog_count > 1 && ` · ${order.dog_count} dogs`}
          {order.requested_pickup_date && ` · ${order.requested_pickup_date}`}
          {order.amount_usd && ` · $${order.amount_usd.toFixed(2)}`}
        </p>
        {order.notes && (
          <p className="text-[11px] text-[#9A9A9A] mt-0.5 italic line-clamp-1">{order.notes}</p>
        )}
      </div>
      {renderAction && <div className="flex-shrink-0">{renderAction(order)}</div>}
    </div>
  );
}

function AssignDialog({ order, transporters, onAssigned, onCancel }) {
  const [open, setOpen] = useState(false);
  const [transporterId, setTransporterId] = useState("");
  const [amount, setAmount] = useState("");
  const [pickupEta, setPickupEta] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post(`/admin/transport-orders/${order.id}/assign`, {
        transporter_id: transporterId,
        amount_usd: Number(amount),
        pickup_eta: pickupEta || null,
        notes: notes || null,
        origin_url: webOrigin(),
      });
      toast.success("Assigned + checkout created", {
        description: "Email the checkout link to the buyer.",
      });
      onAssigned(res.data.order);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't assign.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex gap-2">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button size="sm" className="rounded-full bg-ink hover:bg-crimson text-white text-[12px] h-8" data-testid={`assign-btn-${order.id}`}>
            Assign transporter
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign + price this trip</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3" data-testid="assign-form">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Transporter *</Label>
              <select
                value={transporterId}
                onChange={(e) => setTransporterId(e.target.value)}
                required
                className="mt-1 w-full border border-[#E5E5E5] rounded-md px-3 h-10 text-[13px] bg-white"
                data-testid="assign-transporter-select"
              >
                <option value="">Select…</option>
                {transporters.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.display_name} ({t.service_area_states?.join("/") || "—"})
                  </option>
                ))}
              </select>
              {transporters.length === 0 && (
                <p className="text-[11px] text-crimson mt-1">
                  No payout-ready transporters available. Verify Stripe Connect first.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Price (USD) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  className="mt-1"
                  placeholder="375.00"
                  data-testid="assign-amount"
                />
                <p className="text-[10px] text-[#9A9A9A] mt-1">
                  Platform takes 10% (${(Number(amount) * 0.1 || 0).toFixed(2)}); transporter nets ${(Number(amount) * 0.9 || 0).toFixed(2)}.
                </p>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Pickup ETA</Label>
                <Input
                  type="datetime-local"
                  value={pickupEta}
                  onChange={(e) => setPickupEta(e.target.value)}
                  className="mt-1"
                  data-testid="assign-pickup-eta"
                />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Notes for buyer</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="mt-1"
                data-testid="assign-notes"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting || !transporterId || !amount}
              className="w-full rounded-full bg-crimson hover:bg-ink text-white"
              data-testid="assign-submit"
            >
              {submitting ? "Creating checkout…" : "Assign + create checkout link"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      <Button
        size="sm"
        variant="outline"
        className="rounded-full text-[12px] h-8 text-[#6B6B6B]"
        onClick={() => onCancel(order.id)}
        data-testid={`cancel-btn-${order.id}`}
      >
        Cancel
      </Button>
    </div>
  );
}

function CopyCheckoutLink({ order }) {
  const copy = () => {
    navigator.clipboard.writeText(order.stripe_checkout_url);
    toast.success("Checkout link copied", { description: "Paste into an email to the buyer." });
  };
  if (!order.stripe_checkout_url) return null;
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={copy}
      className="rounded-full text-[12px] h-8"
      data-testid={`copy-checkout-${order.id}`}
    >
      <Copy size={12} className="mr-1" /> Copy link
    </Button>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, testId }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">{label}</Label>
      <Input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="mt-1 text-[13px]"
        data-testid={testId}
      />
    </div>
  );
}

function TransporterRow({ t, onStatus, onReload }) {
  const refreshStripe = async () => {
    try {
      const { data } = await api.post(`/admin/transporters/${t.id}/refresh-stripe`);
      if (data.payouts_enabled) {
        toast.success(data.changed
          ? "Stripe IS complete — flag was stale (missed webhook). Fixed."
          : "Stripe complete — status already in sync.");
      } else {
        const due = (data.requirements_currently_due_labels || []).slice(0, 3).join(", ");
        toast.info(`Stripe confirms setup is NOT complete.${due ? ` Still needed: ${due}` : ""}`);
      }
      onReload && onReload();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't refresh from Stripe.");
    }
  };
  const tone = STATUS_TONE[t.status] || "text-[#6B6B6B] bg-[#FAFAFA] border-[#E5E5E5]";
  return (
    <div
      className="border border-[#EAEAEA] rounded-xl p-4"
      data-testid={`admin-transporter-row-${t.id}`}
    >
      <div className="flex items-start gap-3">
        <span
          className="flex items-center justify-center rounded-lg flex-shrink-0"
          style={{
            width: 44, height: 44, background: "#FBF1D8",
            border: "1px solid rgba(201,162,39,0.45)",
          }}
        >
          <ShieldCheck size={22} weight="duotone" className="text-gold-dark" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-spectral text-lg tracking-tight leading-snug">{t.display_name}</p>
          <div className="flex items-center gap-1.5 flex-wrap mt-1">
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${tone}`}>
            {t.status}
          </span>
          {t.stripe_payouts_enabled && (
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700">
              Stripe ready
            </span>
          )}
          {!t.stripe_payouts_enabled && (
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-yellow-50 border border-yellow-200 text-yellow-800" data-testid={`transporter-stripe-pending-${t.id}`}>
              Stripe pending
            </span>
          )}
          {t.has_account === false && (
            <span
              className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-[#FDECEC] border border-[#F2C4C4] text-crimson"
              title="Approved from a public application — they haven't created a platform account with this email yet, so they won't appear on the Users or Personas pages."
              data-testid={`transporter-no-account-${t.id}`}
            >
              No account yet
            </span>
          )}
          </div>
          <p className="text-[12px] text-[#6B6B6B] mt-1.5 truncate">
            {t.email}{t.phone ? ` · ${t.phone}` : ""}
          </p>
          <p className="text-[11px] text-[#6B6B6B] mt-0.5">
            USDA {t.usda_license_number}
            {t.insurance_liability_usd && ` · $${(t.insurance_liability_usd / 1000).toFixed(0)}K liability (${t.insurance_carrier})`}
            {` · ${t.trips_completed || 0} trips`}
          </p>
          {t.service_area_states?.length > 0 && (
            <p className="text-[11px] text-[#6B6B6B] mt-0.5 truncate">
              Service area: {t.service_area_states.join(", ")}
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#F2F2F2]">
        {t.stripe_connect_account_id && (
          <button
            type="button"
            onClick={refreshStripe}
            className="text-[11px] font-semibold px-3 py-1 rounded-full border border-ink/30 text-ink hover:bg-ink hover:text-white inline-flex items-center gap-1 transition-colors"
            data-testid={`refresh-stripe-${t.id}`}
            title="Pull live status from Stripe and fix a stale payouts flag (missed webhook)"
          >
            Refresh from Stripe
          </button>
        )}
        <a
          href={`/transporter?as=${encodeURIComponent(t.email)}`}
          className="text-[11px] font-semibold px-3 py-1 rounded-full border border-ink/30 text-ink hover:bg-ink hover:text-white inline-flex items-center gap-1 transition-colors"
          data-testid={`view-as-${t.id}`}
          title={`Open ${t.display_name}'s dashboard as admin`}
        >
          View dashboard
        </a>
        {t.status !== "active" && (
          <button
            onClick={() => onStatus(t.id, "active")}
            className="text-[11px] font-semibold px-3 py-1 rounded-full border border-emerald-200 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1"
            data-testid={`status-active-${t.id}`}
          >
            <CheckCircle size={12} /> Activate
          </button>
        )}
        {t.status !== "paused" && (
          <button
            onClick={() => onStatus(t.id, "paused")}
            className="text-[11px] font-semibold px-3 py-1 rounded-full border border-amber-200 text-amber-700 hover:bg-amber-50 inline-flex items-center gap-1"
            data-testid={`status-paused-${t.id}`}
          >
            <Pause size={12} /> Pause
          </button>
        )}
        {t.status !== "suspended" && t.status !== "terminated" && (
          <button
            onClick={() => onStatus(t.id, "suspended")}
            className="text-[11px] font-semibold px-3 py-1 rounded-full border border-orange-200 text-orange-700 hover:bg-orange-50 inline-flex items-center gap-1"
            data-testid={`status-suspended-${t.id}`}
          >
            <Prohibit size={12} /> Suspend
          </button>
        )}
      </div>
    </div>
  );
}


/**
 * ApplicationsPanel — admin review queue for self-service applications.
 *
 * Three statuses surface separately: pending (action required),
 * needs_info (admin already asked, waiting on applicant), approved /
 * rejected (audit trail, collapsible).
 *
 * Each row has a Review button that opens a modal with all
 * applicant-provided fields + Approve / Reject / Needs-info actions.
 * Approval creates a Stripe Connect Express account + emails the
 * onboarding link via Resend.
 */
function ApplicationsPanel() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/transporter-applications");
      setApps(res.data.applications || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't load applications.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Skeleton className="h-40" />;

  const groups = {
    pending: apps.filter((a) => a.review_status === "pending"),
    needs_info: apps.filter((a) => a.review_status === "needs_info"),
    approved: apps.filter((a) => a.review_status === "approved"),
    rejected: apps.filter((a) => a.review_status === "rejected"),
  };

  return (
    <div className="space-y-6" data-testid="applications-panel">
      <ApplicationGroup
        title="Pending review" tone="amber"
        apps={groups.pending} onReview={setReviewing}
        emptyHint="No new applications. Share /transport/apply with prospective haulers."
      />
      <ApplicationGroup
        title="Waiting on applicant" tone="blue"
        apps={groups.needs_info} onReview={setReviewing} emptyHint=""
      />
      <ApplicationGroup
        title="Approved" tone="emerald"
        apps={groups.approved} onReview={setReviewing} emptyHint="" collapsed
      />
      <ApplicationGroup
        title="Rejected" tone="red"
        apps={groups.rejected} onReview={setReviewing} emptyHint="" collapsed
      />
      {reviewing && (
        <ReviewDialog
          app={reviewing}
          open={!!reviewing}
          onOpenChange={(v) => !v && setReviewing(null)}
          onDecided={() => { setReviewing(null); load(); }}
        />
      )}
    </div>
  );
}

function ApplicationGroup({ title, tone, apps, onReview, emptyHint, collapsed }) {
  const [expanded, setExpanded] = useState(!collapsed);
  if (apps.length === 0 && !emptyHint) return null;
  return (
    <section className="border border-[#E5E5E5] bg-white rounded-2xl p-5">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-spectral text-lg tracking-tight">
          {title}
          <span className={`ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border bg-${tone}-50 border-${tone}-200 text-${tone}-700`}>
            {apps.length}
          </span>
        </h3>
        {collapsed && (
          <button onClick={() => setExpanded(!expanded)} className="text-[12px] text-[#6B6B6B] hover:text-ink">
            {expanded ? "Hide" : "Show"}
          </button>
        )}
      </div>
      {expanded && (
        apps.length === 0 ? (
          <p className="text-[12px] text-[#6B6B6B]">{emptyHint}</p>
        ) : (
          <div className="space-y-3">
            {apps.map((a) => (
              <div
                key={a.id}
                className="border border-[#EAEAEA] rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                data-testid={`application-row-${a.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[14px]">{a.display_name}</p>
                  <p className="text-[12px] text-[#6B6B6B] mt-0.5">
                    {a.email}
                    {a.usda_license_number && ` · USDA ${a.usda_license_number}`}
                    {a.insurance_liability_usd && ` · $${(a.insurance_liability_usd/1000).toFixed(0)}K liability`}
                  </p>
                  <p className="text-[11px] text-[#9A9A9A] mt-0.5">
                    Submitted {new Date(a.submitted_at).toLocaleString()}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => onReview(a)}
                  className="rounded-full bg-ink hover:bg-crimson text-white text-[12px] h-8"
                  data-testid={`review-btn-${a.id}`}
                >
                  Review
                </Button>
              </div>
            ))}
          </div>
        )
      )}
    </section>
  );
}

function ReviewDialog({ app, open, onOpenChange, onDecided }) {
  const [reason, setReason] = useState(app.reviewer_notes || "");
  const [busy, setBusy] = useState(false);

  const decide = async (path, requireReason) => {
    if (requireReason && (!reason || reason.trim().length < 10)) {
      toast.error("Please write a specific reason (min 10 chars).");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/transporter-applications/${app.id}/${path}`, {
        reason: reason || null,
      });
      const msg = path === "approve" ? "Approved — onboarding email sent."
        : path === "reject" ? "Rejected — applicant notified."
        : "Info request sent.";
      toast.success(msg);
      onDecided();
    } catch (err) {
      toast.error(formatApiError(err) || "Action failed.");
    } finally {
      setBusy(false);
    }
  };

  const isFinal = ["approved", "rejected"].includes(app.review_status);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{app.display_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-[13px]" data-testid="review-dialog-body">
          <div className="grid sm:grid-cols-2 gap-3 text-[12px]">
            <Detail label="Email" value={app.email} />
            <Detail label="Phone" value={app.phone || "—"} />
            <Detail label="USDA #" value={app.usda_license_number} />
            <Detail label="USDA expiry" value={app.usda_license_expiry || "—"} />
            <Detail label="Insurance carrier" value={app.insurance_carrier} />
            <Detail label="Policy #" value={app.insurance_policy_number} />
            <Detail label="Liability"
              value={`$${(app.insurance_liability_usd/1000).toFixed(0)}K`} />
            <Detail label="Insurance expiry" value={app.insurance_expiry || "—"} />
          </div>
          <div className="flex flex-wrap gap-2">
            <DocLink label="USDA license" url={app.usda_license_url} />
            <DocLink label="Insurance cert" url={app.insurance_certificate_url} />
            {app.rig_photo_url && <DocLink label="Rig photo" url={app.rig_photo_url} />}
            {app.kennel_photo_url && <DocLink label="Kennel photo" url={app.kennel_photo_url} />}
          </div>
          {app.service_area_states?.length > 0 && (
            <Detail label="Service area" value={app.service_area_states.join(" · ")} />
          )}
          {app.extra_notes && <Detail label="Notes" value={app.extra_notes} />}
          {!isFinal && (
            <>
              <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mt-3 block">
                Reviewer note (required for reject / needs-info)
              </Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="mt-1"
                placeholder="What's wrong or what do you need from them?"
                data-testid="review-reason"
              />
              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  onClick={() => decide("approve", false)}
                  disabled={busy}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="review-approve"
                >
                  Approve + onboard
                </Button>
                <Button
                  variant="outline"
                  onClick={() => decide("needs-info", true)}
                  disabled={busy}
                  className="rounded-full"
                  data-testid="review-needs-info"
                >
                  Request more info
                </Button>
                <Button
                  variant="outline"
                  onClick={() => decide("reject", true)}
                  disabled={busy}
                  className="rounded-full text-crimson border-crimson hover:bg-[#FDECEC]"
                  data-testid="review-reject"
                >
                  Reject
                </Button>
              </div>
            </>
          )}
          {isFinal && (
            <div className="border border-[#E5E5E5] bg-[#FAFAFA] rounded p-3 text-[12px] text-[#6B6B6B]">
              <strong>{app.review_status === "approved" ? "Approved" : "Rejected"}</strong>{" "}
              on {new Date(app.reviewed_at).toLocaleString()}.
              {app.reviewer_notes && (<p className="mt-1">Note: {app.reviewer_notes}</p>)}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">{label}</p>
      <p className="text-[13px]">{value}</p>
    </div>
  );
}

function DocLink({ label, url }) {
  return (
    <a
      href={url}
      target="_blank" rel="noreferrer noopener"
      className="text-[12px] font-semibold inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-ink text-ink hover:bg-ink hover:text-white transition-colors"
    >
      {label} ↗
    </a>
  );
}
